import { ENDPOINT, endpointWrapper } from './meta.api';
import {
    createHealthStat,
    getInstagramHealthStats,
    getInstagramRelatedHealthStats,
} from 'state/utils';
import { isNew } from 'utils/posts';
import { tooltips } from '@/copy';

export const getInstagramData = async (config) =>
    endpointWrapper({
        domain: 'instagram/data_points',
        ...config,
    }).then(({ data, ...rest }) => ({
        data: data.map(({ data, ...rest }) => {
            const flatData = { ...rest, ...data };
            const healthStats = getInstagramHealthStats(flatData);
            return {
                ...flatData,
                healthStats,
            };
        }),
        ...rest,
    }));

export const getInstagramBrands = async (config) =>
    endpointWrapper({
        domain: 'instagram/brands',
        ...config,
    });

export const getInstagramRelatedHashtagData = async (config) =>
    endpointWrapper({
        domain: 'instagram/related_hashtags',
        ...config,
    }).then(({ data, ...rest }) => ({
        data: data.map(({ data, ...rest }) => ({
            ...rest,
            ...data,
        })),
        ...rest,
    }));

export const getInstagramRelatedHashtagGroupData = async (config) =>
    endpointWrapper({
        domain: 'instagram/related_hashtag_groups',
        ...config,
    }).then(({ data, ...rest }) => ({
        data: data.map(({ data, ...rest }) => ({
            ...rest,
            ...data,
        })),
        ...rest,
    }));
function createPaidTypesMessage({ is_ad, partner_hashtag }) {
    let paidTypesMessage;
    if (is_ad == 1 && partner_hashtag) {
        paidTypesMessage = tooltips.isBrandPartnerAndPaidPartnershipInstagram;
    } else if (is_ad == 1) {
        paidTypesMessage = tooltips.isOnlyPaidPartnershipInstagram;
    } else if (partner_hashtag) {
        paidTypesMessage = tooltips.isOnlyBrandPartnerInstagram;
    }
    return paidTypesMessage;
}

const shapePosts = (resp) => {
    if (resp?.data?.length == 0) return [];
    return resp.data.map(({ data, ...post }) => {
        return {
            ...post,
            ...data,
            isPaid: !!(data?.is_ad == 1 || data?.partner_hashtag),
            isNew: isNew({
                week: post.week,
                year: post.year,
                create_timestamp: data?.create_timestamp,
            }),
            paidTypesMessage: createPaidTypesMessage(data),
        };
    });
};

export const getCategoryTopPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/category_top_posts',
        ...config,
    }).then(shapePosts);

export const getBrandCat1TopPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/brand_cat1_top_posts',
        ...config,
    }).then(shapePosts);

export const getBrandOnlyTopPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/brand_only_top_posts',
        ...config,
    }).then(shapePosts);

export const getHashtagTopPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/hashtag_top_posts',
        ...config,
    }).then(shapePosts);

export const getCategoryBrandRelatedHashtagTopPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/category_brand_related_hashtag_top_posts',
        ...config,
    }).then(shapePosts);

export const getBrandOnlyRelatedHashtagTopPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/brand_only_related_hashtag_top_posts',
        ...config,
    }).then(shapePosts);

export const getCategoryRelatedHashtagTopPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/category_related_hashtag_top_posts',
        ...config,
    }).then(shapePosts);

export const getCategoryRelatedCategoryTopPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/category_related_category_top_posts',
        ...config,
    }).then(shapePosts);

export const getBrandRelatedCategoryTopPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/brand_related_category_top_posts',
        ...config,
    }).then(shapePosts);

export const getCategoryRelatedBrandTopPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/category_related_brand_top_posts',
        ...config,
    }).then(shapePosts);

// -----------------------------------------------------------------------------
// Keyword + hashtag post aggregation surfaces (v2 IG keyword-matching tables).
//
// Same pattern as the TT side (see `tiktok.api.js`). BE returns lowercase jsonb
// keys (`wow`, `mom`, `likes`, `avg_weekly_likes`). We alias them to the legacy
// `non_cum_*_rolling` shape so the existing `TIKTOK_METADATA_BY_KEY` /
// `non_cum_likes_*` metric configs keep working unchanged.
// -----------------------------------------------------------------------------

const RELATED_AGGREGATE_FE_TO_BE_ORDER_BY = {
    non_cum_avg_weekly_likes: 'avg_weekly_likes',
    non_cum_likes_WoW_rolling: 'wow',
    non_cum_likes_increase_WoW_rolling: 'increase_wow',
    non_cum_likes_MoM_rolling: 'mom',
    non_cum_likes_increase_MoM_rolling: 'increase_mom',
    non_cum_likes_QoQ_rolling: 'qoq',
    non_cum_likes_increase_QoQ_rolling: 'increase_qoq',
    non_cum_likes_YoY_rolling: 'yoy',
    non_cum_likes_increase_YoY_rolling: 'increase_yoy',
};

const translateRelatedAggregateOrderBy = (data) => {
    if (!data || !data.order_by) return data;
    return {
        ...data,
        order_by:
            RELATED_AGGREGATE_FE_TO_BE_ORDER_BY[data.order_by] || data.order_by,
    };
};

const reshapeRelatedAggregateData = (data = {}) => ({
    likes: data.likes,
    non_cum_avg_weekly_likes: data.avg_weekly_likes,
    non_cum_likes_WoW_rolling: data.wow,
    non_cum_likes_increase_WoW_rolling: data.increase_wow,
    non_cum_likes_MoM_rolling: data.mom,
    non_cum_likes_increase_MoM_rolling: data.increase_mom,
    non_cum_likes_QoQ_rolling: data.qoq,
    non_cum_likes_increase_QoQ_rolling: data.increase_qoq,
    non_cum_likes_YoY_rolling: data.yoy,
    non_cum_likes_increase_YoY_rolling: data.increase_yoy,
    affinity_group: data.affinity_group,
    hs_avg_weekly_likes: data.hs_avg_weekly_likes,
});

const shapeInstagramRelatedCategoryRow = ({
    id,
    country,
    category,
    brand,
    related_category,
    year,
    week,
    related_category_name,
    brand_name,
    price_segmentation,
    related_type,
    parent_name,
    data,
}) => {
    const reshapedData = reshapeRelatedAggregateData(data);
    const pricePointHealthStat = brand_name
        ? createHealthStat('price point', { price_segmentation })
        : null;
    return {
        id,
        countryId: country,
        categoryId: category,
        brandId: brand,
        relatedCategoryId: related_category,
        year,
        week,
        name: related_category_name,
        brandName: brand_name,
        ...reshapedData,
        healthStats: {
            ...getInstagramRelatedHealthStats(reshapedData),
            ...(pricePointHealthStat && {
                'price point': pricePointHealthStat,
            }),
        },
        ...(pricePointHealthStat && {
            price_segmentation: pricePointHealthStat.score,
        }),
        ...(related_type && { related_type }),
        ...(parent_name && { parent_name }),
        parentName: (data?.affinity_group || []).map((s) => s.toLowerCase()),
        fetching: false,
        fetchingData: false,
    };
};

const shapeInstagramRelatedBrandRow = ({
    id,
    country,
    category,
    related_brand,
    year,
    week,
    related_brand_name,
    price_segmentation,
    parent_name,
    data,
}) => {
    const reshapedData = reshapeRelatedAggregateData(data);
    const pricePointHealthStat = createHealthStat('price point', {
        price_segmentation,
    });
    return {
        id,
        countryId: country,
        categoryId: category,
        relatedBrandId: related_brand,
        year,
        week,
        name: related_brand_name,
        brandName: related_brand_name,
        ...reshapedData,
        healthStats: {
            ...getInstagramRelatedHealthStats(reshapedData),
            'price point': pricePointHealthStat,
        },
        price_segmentation: pricePointHealthStat?.score,
        ...(parent_name && { parent_name }),
        parentName: [],
        fetching: false,
        fetchingData: false,
    };
};

const shapeInstagramRelatedCategoryGroupRow = ({
    id,
    country,
    category,
    brand,
    related_category_group,
    year,
    week,
    data,
}) => ({
    id,
    countryId: country,
    categoryId: category,
    brandId: brand,
    relatedCategoryGroup: related_category_group,
    year,
    week,
    name: related_category_group,
    ...reshapeRelatedAggregateData(data),
    parentName: [],
});

const shapeInstagramBreakdownHashtagRow = ({
    id,
    country,
    category,
    brand,
    related_category,
    related_brand,
    hashtag,
    year,
    week,
    data,
}) => {
    return {
        id,
        countryId: country,
        categoryId: category,
        brandId: brand,
        relatedCategoryId: related_category,
        relatedBrandId: related_brand,
        name: hashtag,
        hashtagName: hashtag,
        year,
        week,
        likes: data?.likes,
        volume: data?.likes,
    };
};

const shapeInstagramBreakdownKeywordRow = ({
    id,
    country,
    category,
    brand,
    related_category,
    related_brand,
    keyword,
    year,
    week,
    data,
}) => ({
    id,
    countryId: country,
    categoryId: category,
    brandId: brand,
    relatedCategoryId: related_category,
    relatedBrandId: related_brand,
    name: keyword,
    keyword,
    year,
    week,
    likes: data?.likes,
    volume: data?.likes,
});

const mapInstagramDataResponse = (resp, shaper) => {
    const { data } = resp;
    if (!(data && data.length)) return resp;
    return { ...resp, data: data.map(shaper) };
};

// Affinity-groups dropdown source --------------------------------------

export const getInstagramRelatedAffinityGroups = async (config) =>
    endpointWrapper({
        domain: 'instagram/related_affinity_groups',
        ...config,
    });

// Watchlist enrichment: latest aggregate row per saved (parent, related) pair.
export const getInstagramRelatedCategoryPairs = async (config) =>
    endpointWrapper({
        domain: 'instagram/data_points_related_category_pairs',
        ...config,
    }).then((resp) =>
        mapInstagramDataResponse(resp, shapeInstagramRelatedCategoryRow),
    );

// Aggregate tables ----------------------------------------------------

export const getInstagramRelatedCategoryAggregate = async ({ data, ...rest }) =>
    endpointWrapper({
        domain: 'instagram/related_category',
        data: translateRelatedAggregateOrderBy(data),
        ...rest,
    }).then((resp) =>
        mapInstagramDataResponse(resp, shapeInstagramRelatedCategoryRow),
    );

export const getInstagramRelatedBrandAggregate = async ({ data, ...rest }) =>
    endpointWrapper({
        domain: 'instagram/related_brand',
        data: translateRelatedAggregateOrderBy(data),
        ...rest,
    }).then((resp) =>
        mapInstagramDataResponse(resp, shapeInstagramRelatedBrandRow),
    );

export const getInstagramRelatedCategoryGroupAggregate = async ({
    data,
    ...rest
}) =>
    endpointWrapper({
        domain: 'instagram/related_category_groups',
        data: translateRelatedAggregateOrderBy(data),
        ...rest,
    }).then((resp) =>
        mapInstagramDataResponse(resp, shapeInstagramRelatedCategoryGroupRow),
    );

// Drawer breakdown — keywords ----------------------------------------

export const getInstagramRelatedCategoryKeywords = async (config) =>
    endpointWrapper({
        domain: 'instagram/related_category_keywords',
        ...config,
    }).then((resp) =>
        mapInstagramDataResponse(resp, shapeInstagramBreakdownKeywordRow),
    );

export const getInstagramRelatedBrandKeywords = async (config) =>
    endpointWrapper({
        domain: 'instagram/related_brand_keywords',
        ...config,
    }).then((resp) =>
        mapInstagramDataResponse(resp, shapeInstagramBreakdownKeywordRow),
    );

// Drawer breakdown — hashtags ----------------------------------------

export const getInstagramRelatedCategoryBreakdownHashtags = async (config) =>
    endpointWrapper({
        domain: 'instagram/related_category_breakdown_hashtags',
        ...config,
    }).then((resp) =>
        mapInstagramDataResponse(resp, shapeInstagramBreakdownHashtagRow),
    );

export const getInstagramRelatedBrandBreakdownHashtags = async (config) =>
    endpointWrapper({
        domain: 'instagram/related_brand_breakdown_hashtags',
        ...config,
    }).then((resp) =>
        mapInstagramDataResponse(resp, shapeInstagramBreakdownHashtagRow),
    );

// Drawer breakdown — top posts ---------------------------------------

export const getInstagramRelatedCategoryDrawerTopPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/related_category_top_posts',
        ...config,
    }).then(shapePosts);

export const getInstagramRelatedBrandDrawerTopPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/related_brand_top_posts',
        ...config,
    }).then(shapePosts);

// Drivers of Growth - Time Series Hashtags and Posts by date
export const getTsCategoryHashtags = async (config) =>
    endpointWrapper({
        domain: 'instagram/ts_category_hashtags',
        ...config,
    });

export const getTsBrandHashtags = async (config) =>
    endpointWrapper({
        domain: 'instagram/ts_brand_hashtags',
        ...config,
    });

export const getTsCategoryPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/ts_category_posts',
        ...config,
    });

export const getTsBrandPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/ts_brand_posts',
        ...config,
    });

export const getTsMonthlyCategoryHashtags = async (config) =>
    endpointWrapper({
        domain: 'instagram/ts_monthly_category_hashtags',
        ...config,
    });

export const getTsMonthlyBrandHashtags = async (config) =>
    endpointWrapper({
        domain: 'instagram/ts_monthly_brand_hashtags',
        ...config,
    });

export const getTsMonthlyCategoryPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/ts_monthly_category_posts',
        ...config,
    });

export const getTsMonthlyBrandPosts = async (config) =>
    endpointWrapper({
        domain: 'instagram/ts_monthly_brand_posts',
        ...config,
    });

export const getHashtagRelatedCategory = async (config) =>
    endpointWrapper({
        domain: 'instagram/hashtag_related_category',
        ...config,
    }).then(({ data, ...rest }) => ({
        data: data.map(({ data, ...rest }) => ({
            ...rest,
            ...data,
        })),
        ...rest,
    }));

export const getHashtagRelatedBrand = async (config) =>
    endpointWrapper({
        domain: 'instagram/hashtag_related_brand',
        ...config,
    }).then(({ data, ...rest }) => ({
        data: data.map(({ data, ...rest }) => ({
            ...rest,
            ...data,
        })),
        ...rest,
    }));

export const getHashtagBrandRelatedCategory = async (config) =>
    endpointWrapper({
        domain: 'instagram/hashtag_brand_related_category',
        ...config,
    }).then(({ data, ...rest }) => ({
        data: data.map(({ data, ...rest }) => ({
            ...rest,
            ...data,
        })),
        ...rest,
    }));

export const getInstagramHashtagsByName = async (config) =>
    endpointWrapper({
        domain: 'instagram/data_points/hashtags_by_name',
        ...config,
    }).then(({ data, ...rest }) => ({
        data: data.map(({ data, ...rest }) => ({
            ...rest,
            ...data,
        })),
        ...rest,
    }));

export const getInstagramRelatedHashtagDataInPairs = async (config) =>
    endpointWrapper({
        domain: 'instagram/related_hashtags_pairs',
        ...config,
    }).then((resp) => {
        const { data } = resp;
        if (!(data && data.length)) return resp;
        return {
            ...resp,
            data: data.map(
                ({
                    id,
                    hashtag,
                    brand,
                    country,
                    brand_name,
                    category,
                    category_name,
                    data,
                }) => {
                    return {
                        id,
                        brand,
                        country,
                        name: hashtag,
                        hashtag,
                        brandName: brand_name,
                        categoryName: category_name,
                        category,
                        ...data,
                        fetching: false,
                        fetchingData: false,
                        relatedName: hashtag,
                    };
                },
            ),
        };
    });

export const getThumbnail = async (shortcode) => {
    const response = await fetch(
        `${ENDPOINT}/instagram/thumbnail/${shortcode}/`,
        {
            method: 'GET',
        },
    );
    if (!response.ok) {
        let message;
        if (response.status === 404) {
            message = `Thumbnail not found for post ${shortcode}`;
        } else if (response.status === 503) {
            message = `Service temporarily unavailable for thumbnail ${shortcode} (status ${response.status})`;
        } else if (response.status >= 500) {
            message = `Server error loading thumbnail for ${shortcode} (status ${response.status})`;
        } else {
            message = `Failed to load thumbnail for ${shortcode} (status ${response.status})`;
        }
        const error = new Error(message);
        error.status = response.status;
        throw error;
    }
    return response.blob();
};
